import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { keys as resourceSetKeys } from 'components/IAP/hooks/resourceSetHooks';
import type {
  AssetImportBatchSummary,
  AssetImportProvenance,
} from './assetImportLabels';
import type {
  AssetColumnMapping,
  AssetImportDefaultsInput,
  GeocodeMode,
  ParsedImportFile,
} from './assetImportDialogModel';
import type { PreviewRow } from 'components/DisasterGPT/agentConfirm/types';

export const assetImportKeys = {
  all: ['org_asset_imports'] as const,
  provenance: (groupGuid: string, ids: string[]) =>
    [...assetImportKeys.all, 'provenance', groupGuid, ids] as const,
  batch: (groupGuid: string, batchId: string) =>
    [...assetImportKeys.all, 'batch', groupGuid, batchId] as const,
};

export function useAssetImportProvenance(resourceSetIds: string[]) {
  const groupGuid = useGroupGuid();
  const ids = [...new Set(resourceSetIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: assetImportKeys.provenance(groupGuid, ids),
    queryFn: async () => {
      const { data } = await axios.get<AssetImportProvenance[]>(
        `${config.apiGateway.org_asset_imports}/provenance`,
        { params: { groupGuid, resourceSetIds: ids.join(',') } }
      );
      return data;
    },
    enabled: !!groupGuid && ids.length > 0,
    staleTime: 60_000,
  });
}

export function useAssetImportBatch(batchId: string | null | undefined) {
  const groupGuid = useGroupGuid();
  return useQuery({
    queryKey: assetImportKeys.batch(groupGuid, batchId ?? ''),
    queryFn: async () => {
      const { data } = await axios.get<AssetImportBatchSummary>(
        `${config.apiGateway.org_asset_imports}/${encodeURIComponent(batchId!)}`,
        { params: { groupGuid } }
      );
      return data;
    },
    enabled: !!groupGuid && !!batchId,
    retry: false,
  });
}

export interface AssetImportPreview {
  batchId: string;
  counts: Record<string, number>;
  rows: PreviewRow[];
}

export interface AssetImportPreviewInput {
  file: File;
  sheet: string;
  mapping: AssetColumnMapping;
  defaults: AssetImportDefaultsInput;
  geocodeMode: GeocodeMode;
}

export interface AssetImportCommitInput {
  batchId: string;
  include_rows: number[];
  update_existing: boolean;
  saveMapping: boolean;
}

export interface AssetImportCommitResponse {
  result: {
    created: number;
    updated: number;
    skipped: number;
    failed: number;
    errors: string[];
  };
  batch: AssetImportBatchSummary;
}

function uploadForm(file: File, groupGuid: string): FormData {
  const form = new FormData();
  form.append('groupGuid', groupGuid);
  form.append('file', file);
  return form;
}

/**
 * Read an uploaded spreadsheet's sheets, headers and suggested or saved
 * mapping. Stores nothing.
 */
export function useParseAssetFile() {
  const groupGuid = useGroupGuid();
  return useMutation({
    mutationFn: async (file: File) => {
      const { data } = await axios.post<ParsedImportFile>(
        `${config.apiGateway.org_asset_imports}/parse`,
        uploadForm(file, groupGuid)
      );
      return data;
    },
  });
}

/** Draft, geocode and check every row into an import batch. Writes no assets. */
export function usePreviewAssetImport() {
  const groupGuid = useGroupGuid();
  return useMutation({
    mutationFn: async (input: AssetImportPreviewInput) => {
      const form = uploadForm(input.file, groupGuid);
      form.append('sheet', input.sheet);
      form.append('mapping', JSON.stringify(input.mapping));
      form.append('defaults', JSON.stringify(input.defaults));
      form.append('geocodeMode', input.geocodeMode);
      const { data } = await axios.post<AssetImportPreview>(
        `${config.apiGateway.org_asset_imports}/preview`,
        form
      );
      return data;
    },
  });
}

/** Save the selected rows of a previewed import. */
export function useCommitAssetImport() {
  const groupGuid = useGroupGuid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ batchId, ...body }: AssetImportCommitInput) => {
      const { data } = await axios.post<AssetImportCommitResponse>(
        `${config.apiGateway.org_asset_imports}/${encodeURIComponent(batchId)}/commit`,
        { groupGuid, ...body }
      );
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceSetKeys.all });
      queryClient.invalidateQueries({ queryKey: assetImportKeys.all });
    },
  });
}

/** Download the template CSV with the headers the importer recognizes. */
export async function downloadAssetImportTemplate() {
  const { data } = await axios.get<Blob>(
    `${config.apiGateway.org_asset_imports}/template.csv`,
    { responseType: 'blob' }
  );
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'asset-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** Archive every asset an import created. */
export function useUndoAssetImport() {
  const groupGuid = useGroupGuid();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batchId: string) => {
      const { data } = await axios.post<{
        archived: number;
        already_archived: number;
      }>(
        `${config.apiGateway.org_asset_imports}/${encodeURIComponent(batchId)}/undo`,
        { groupGuid }
      );
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceSetKeys.all });
      queryClient.invalidateQueries({ queryKey: assetImportKeys.all });
    },
  });
}
